import { pillarArticles } from '@/mocks/pillarArticles';

// The real site origin comes from the platform-managed environment variable.
// Fallback to the known production domain only when it is not yet injected.
export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://cssbuybr.lol';

export const SITE_NAME = 'CSSBuy Spreadsheet';

const DEFAULT_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=Premium%20streetwear%20fashion%20flat%20lay%20with%20sneakers%20hoodies%20and%20accessories%20on%20warm%20beige%20marble%20surface%20editorial%20product%20photography%20soft%20natural%20lighting%20minimalist%20aesthetic%20warm%20neutral%20tones&width=1600&height=900&seq=hero-bg&orientation=landscape';

export interface SeoMeta {
  title: string;
  description: string;
  keywords: string;
  path: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
}

const baseKeywords =
  'CSSBuy Spreadsheet, cssbuy review, cssbuy shipping, cssbuy guide, cssbuy qc, product research';

const staticSeo: Record<string, SeoMeta> = {
  '/': {
    title: 'CSSBuy Spreadsheet 2026 | Guias, Produtos, QC & Envio',
    description:
      'Explore o CSSBuy Spreadsheet com guias de pesquisa de produtos, dicas de QC, informações de envio, comparações, recursos de segurança e guias práticos de compra.',
    keywords: baseKeywords + ', buying agent',
    path: '/',
  },
  '/en': {
    title: 'CSSBuy Spreadsheet 2026 | Guides, Products, QC & Shipping',
    description:
      'Explore the CSSBuy Spreadsheet with product research guides, QC tips, shipping information, comparisons, safety resources and practical buying guides.',
    keywords: baseKeywords + ', buying agent',
    path: '/en',
  },
  '/de': {
    title: 'CSSBuy Spreadsheet 2026 | Anleitungen, Produkte, QC & Versand',
    description:
      'Entdecken Sie das CSSBuy Spreadsheet mit Produktrecherche-Anleitungen, QC-Tipps, Versandinformationen, Vergleichen, Sicherheitsressourcen und praktischen Kaufratgebern.',
    keywords: baseKeywords + ', buying agent',
    path: '/de',
  },
  '/cssbuy-spreadsheet': {
    title: 'CSSBuy Spreadsheet: Recurso Completo de Pesquisa',
    description:
      'Guia completo do CSSBuy Spreadsheet: aprenda a pesquisar produtos, comparar categorias e consultar guias de QC, envio e segurança antes de comprar.',
    keywords: 'CSSBuy Spreadsheet, pesquisa de produtos, comparação de produtos, guia CSSBuy',
    path: '/cssbuy-spreadsheet',
  },
  '/cssbuy-review': {
    title: 'CSSBuy Review 2026: Análise Completa',
    description:
      'CSSBuy Review: análise honesta sobre recursos, QC, warehouse, shipping e custos para quem está começando a comprar através de agentes.',
    keywords: 'CSSBuy review, cssbuy legit, cssbuy pros cons, cssbuy analysis',
    path: '/cssbuy-review',
  },
  '/cssbuy-shipping': {
    title: 'CSSBuy Shipping: Guia de Envio e Custos',
    description:
      'CSSBuy Shipping: entenda os fatores que influenciam o custo de envio, como peso, dimensões, destino e método escolhido.',
    keywords: 'CSSBuy shipping, cssbuy shipping cost, cssbuy shipping guide, shipping lines',
    path: '/cssbuy-shipping',
  },
  '/cssbuy-qc': {
    title: 'CSSBuy QC Guide: Verificação de Qualidade',
    description:
      'CSSBuy QC: guia de controle de qualidade para verificar fotos, comparar com o anúncio e aprovar ou rejeitar itens antes do envio.',
    keywords: 'CSSBuy QC, cssbuy quality control, qc photos, qc checklist',
    path: '/cssbuy-qc',
  },
  '/cssbuy-guide': {
    title: 'CSSBuy Guide: Guia Completo para Iniciantes',
    description:
      'CSSBuy Guide: guia de compra para iniciantes com passos práticos para pesquisar, comparar e comprar com mais segurança.',
    keywords: 'CSSBuy guide, cssbuy beginner guide, how to use cssbuy',
    path: '/cssbuy-guide',
  },
  '/cssbuy-safety': {
    title: 'CSSBuy Safety Center: Pesquise com Segurança',
    description:
      'CSSBuy Safety Center: recomendações de segurança para verificar URLs, proteger sua conta e evitar golpes durante a compra.',
    keywords: 'CSSBuy safety, cssbuy legit, cssbuy secure, buy safely',
    path: '/cssbuy-safety',
  },
  '/cssbuy-faq': {
    title: 'CSSBuy FAQ: Perguntas Frequentes',
    description:
      'CSSBuy FAQ: respostas para as perguntas mais comuns sobre o CSSBuy Spreadsheet, pesquisa de produtos, envio, QC e segurança.',
    keywords: 'CSSBuy FAQ, cssbuy questions, cssbuy help',
    path: '/cssbuy-faq',
  },
  '/categories': {
    title: 'Categorias CSSBuy Spreadsheet',
    description:
      'Explore todas as categorias do CSSBuy Spreadsheet: Shoes, Hoodies, T-Shirts, Jackets, Bags, Accessories e muito mais.',
    keywords: 'CSSBuy categories, cssbuy spreadsheet categories, product categories',
    path: '/categories',
  },
  '/cssbuy-spreadsheet-beginners': {
    title: 'CSSBuy Spreadsheet para Iniciantes: Guia Passo a Passo',
    description:
      'CSSBuy Spreadsheet para iniciantes: guia completo para começar a pesquisar produtos de forma organizada, segura e eficiente.',
    keywords: 'CSSBuy Spreadsheet, cssbuy beginners, cssbuy guide, product research',
    path: '/cssbuy-spreadsheet-beginners',
    ogImage:
      'https://readdy.ai/api/search-image?query=Person%20learning%20to%20research%20fashion%20products%20on%20laptop%20with%20spreadsheet%20and%20notes%20warm%20minimal%20workspace%20editorial%20photography%20soft%20natural%20lighting%20clean%20aesthetic&width=1200&height=600&seq=article-beginners-hero&orientation=landscape',
    ogType: 'article',
  },
  '/about': {
    title: 'Sobre o CSSBuy Spreadsheet',
    description:
      'Sobre o CSSBuy Spreadsheet: nossa missão, o que oferecemos e como ajudamos você a pesquisar produtos com mais facilidade e segurança.',
    keywords: 'CSSBuy Spreadsheet, about cssbuy, sobre',
    path: '/about',
  },
  '/contact': {
    title: 'Contato | CSSBuy Spreadsheet',
    description:
      'Entre em contato com a equipe do CSSBuy Spreadsheet. Envie suas dúvidas, sugestões e feedback.',
    keywords: 'CSSBuy contact, contato cssbuy, feedback',
    path: '/contact',
  },
  '/disclaimer': {
    title: 'Aviso Legal | CSSBuy Spreadsheet',
    description:
      'Aviso legal do CSSBuy Spreadsheet: informações sobre precisão, afiliados e limites de responsabilidade.',
    keywords: 'CSSBuy disclaimer, aviso legal',
    path: '/disclaimer',
  },
  '/privacy-policy': {
    title: 'Política de Privacidade | CSSBuy Spreadsheet',
    description:
      'Política de privacidade do CSSBuy Spreadsheet: como coletamos, usamos e protegemos suas informações.',
    keywords: 'CSSBuy privacy policy, política de privacidade',
    path: '/privacy-policy',
  },
};

export function resolveSeo(pathname: string): SeoMeta {
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  const staticEntry = staticSeo[path];
  if (staticEntry) return staticEntry;

  const slug = path.replace(/^\//, '');
  const article = pillarArticles.find((a) => a.slug === slug);
  if (article) {
    return {
      title: `${article.title} | CSSBuy Spreadsheet`,
      description: article.excerpt,
      keywords: `CSSBuy Spreadsheet, ${article.title}, cssbuy guide, product research`,
      path,
      ogImage: article.image,
      ogType: 'article',
    };
  }

  return {
    title: SITE_NAME,
    description:
      'Explore o CSSBuy Spreadsheet com guias de pesquisa de produtos, QC, envio e comparações.',
    keywords: baseKeywords,
    path,
  };
}

function ensureMeta(attr: 'name' | 'property', key: string): HTMLMetaElement {
  let el = document.head.querySelector(
    `meta[${attr}="${key}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  ensureMeta(attr, key).setAttribute('content', content);
}

function setCanonical(url: string) {
  let link = document.head.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

export function applyHeadMeta(seo: SeoMeta) {
  const url = `${SITE_URL}${seo.path}`;
  const ogImage = seo.ogImage || DEFAULT_OG_IMAGE;

  document.title = seo.title;
  setMeta('name', 'description', seo.description);
  setMeta('name', 'keywords', seo.keywords);
  setCanonical(url);

  setMeta('property', 'og:title', seo.title);
  setMeta('property', 'og:description', seo.description);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:type', seo.ogType || 'website');
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:image', ogImage);

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', seo.title);
  setMeta('name', 'twitter:description', seo.description);
  setMeta('name', 'twitter:image', ogImage);
}